<template>
  <header class="site_header">
    <div class="site_container site_header_bar">
      <nav class="site_header_nav" aria-label="Główny">
        <SiteMenu />
      </nav>

      <router-link to="/" class="site_header_logo" rel="home">
        <img src="/media/logo.svg" width="869" height="396" alt="Japonia Cars" />
      </router-link>

      <div class="site_header_actions">
        <a
          v-for="phone in phones"
          :key="phone"
          class="site_header_phone"
          :href="`tel:${phone.replace(/\s/g, '')}`"
        >
          <PhoneIcon />
          {{ phone }}
        </a>
        <router-link to="/kontakt/" class="site_button site_button--outline">Wyceń auto</router-link>
      </div>

      <button
        class="site_header_burger"
        type="button"
        aria-label="Rozwiń menu"
        :aria-expanded="String(drawerOpen)"
        aria-controls="site_drawer"
        @click="drawerOpen = true"
      >
        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path
            d="M3 13h18c0.552 0 1-0.448 1-1s-0.448-1-1-1h-18c-0.552 0-1 0.448-1 1s0.448 1 1 1zM3 7h18c0.552 0 1-0.448 1-1s-0.448-1-1-1h-18c-0.552 0-1 0.448-1 1s0.448 1 1 1zM3 19h18c0.552 0 1-0.448 1-1s-0.448-1-1-1h-18c-0.552 0-1 0.448-1 1s0.448 1 1 1z"
          />
        </svg>
      </button>
    </div>

    <!-- The drawer stays inside the sticky header: its stacking context spans
         the viewport, so a fixed overlay in here covers the whole page. -->
    <div class="site_drawer" :class="{ is_open: drawerOpen }">
      <div class="site_drawer_overlay" @click="drawerOpen = false"></div>

      <div id="site_drawer" class="site_drawer_panel" role="dialog" aria-modal="true" aria-label="Menu">
        <button class="site_drawer_close" type="button" aria-label="Zamknij menu" @click="drawerOpen = false">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>

        <nav class="site_drawer_nav" aria-label="Główny">
          <SiteMenu />
        </nav>

        <router-link to="/kontakt/" class="site_button">Wyceń auto</router-link>

        <a
          v-for="phone in phones"
          :key="phone"
          class="site_button site_button--outline"
          :href="`tel:${phone.replace(/\s/g, '')}`"
        >
          {{ phone }}
        </a>
      </div>
    </div>
  </header>
</template>

<script>
// The masthead: navigation, logo, contact numbers, and the drawer that takes
// their place on phones.

import SiteMenu from '@/components/SiteMenu.vue'
import PhoneIcon from '@/components/icons/PhoneIcon.vue'

export default {
  name: 'SiteHeader',
  components: { SiteMenu, PhoneIcon },

  data() {
    return {
      drawerOpen: false,
      phones: ['509 230 500', '501 069 442'],
    }
  },

  watch: {
    // Navigating from inside the drawer used to load a new page, which closed
    // it for free. Routing in place does not.
    $route() {
      this.drawerOpen = false
    },

    drawerOpen(open) {
      document.body.classList.toggle('has_drawer_open', open)
    },
  },

  mounted() {
    document.addEventListener('keydown', this.onKeydown)
  },

  beforeUnmount() {
    document.removeEventListener('keydown', this.onKeydown)
    document.body.classList.remove('has_drawer_open')
  },

  methods: {
    onKeydown(event) {
      if (event.key === 'Escape') this.drawerOpen = false
    },
  },
}
</script>

<style scoped>
.site_header {
  position: sticky;
  top: 0;
  z-index: 11;
  background: #fff;
  border-bottom: 1px solid var(--paper-line);
}

.site_header_bar {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  height: 80px;
}

.site_header_logo {
  justify-self: center;
}

.site_header_logo img {
  display: block;
  width: 145px;
  height: auto;
}

.site_header_actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
}

.site_header_phone {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: rgba(10, 10, 11, 0.88);
  white-space: nowrap;
  transition: color 0.2s ease;
}

.site_header_phone:hover {
  color: var(--accent);
}

.site_header_burger {
  display: none;
  justify-self: end;
  padding: 8px;
  border: 0;
  background: none;
  color: var(--ink);
  cursor: pointer;
}

.site_header_burger svg {
  display: block;
  width: 24px;
  height: 24px;
}

/* ------------------------------------------------------------ drawer --- */

.site_drawer {
  position: fixed;
  inset: 0;
  z-index: 100;
  visibility: hidden;
}

.site_drawer.is_open {
  visibility: visible;
}

.site_drawer_overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.site_drawer.is_open .site_drawer_overlay {
  opacity: 1;
}

.site_drawer_panel {
  position: absolute;
  top: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  width: min(350px, 90vw);
  height: 100%;
  padding: 24px;
  background: #0d0f12;
  color: var(--ink-invert);
  transform: translateX(100%);
  transition: transform 0.3s ease;
  overflow-y: auto;
}

.site_drawer.is_open .site_drawer_panel {
  transform: none;
}

.site_drawer_close {
  align-self: flex-end;
  padding: 8px;
  border: 0;
  background: none;
  color: var(--ink-invert);
  cursor: pointer;
}

.site_drawer_close svg {
  display: block;
  width: 24px;
  height: 24px;
}

.site_drawer_nav {
  width: 100%;
  margin-bottom: 8px;
}

.site_drawer_nav :deep(.site_menu) {
  flex-direction: column;
  align-items: stretch;
  gap: 0;
}

.site_drawer_nav :deep(.site_menu a) {
  padding: 16px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
  color: var(--ink-invert);
}

.site_drawer_nav :deep(.site_menu a:hover) {
  color: var(--accent);
}

.site_drawer .site_button--outline {
  border-color: var(--accent);
  color: var(--ink-invert);
}

@media (max-width: 1024px) {
  .site_header_bar {
    grid-template-columns: auto 1fr;
    height: 70px;
  }

  .site_header_nav,
  .site_header_actions {
    display: none;
  }

  .site_header_logo {
    justify-self: start;
  }

  .site_header_burger {
    display: block;
  }
}
</style>
